import React from 'react';
import PropTypes from 'prop-types';
import Comment from '../input/Comment.component';
import {PopUp} from './PopUp.component';
import * as Api from 'utils/api/api';
import {TASK_ACTIONS} from 'utils/constants';
import ErrorDialog from './ErrorDialog.component';
import {connect} from 'react-redux';
import {getTaskDetails, updateTaskDetailsAfterAction} from '../taskdetails-remake/taskDetails.action';
import translate from '../translations/translations.wrapper.jsx';
import * as reduxSelectors from '../store/application.reducers';
import {mapErrorMessage} from 'utils/errorHandle.function';
import { Button, ButtonType } from "components/common/Button.component";
import { FormField } from "components/common/FormField.component";
import { TextInput } from "components/common/TextInput.component";

//this is to make sure the tabbing stays inside the dialog
const TAB_INDEX = 1000;

/**
 * popup which will be used in case user has selected 'add attachment'
 * expects as props:
 * list of selected tasks
 * close popup callback
 * translate function
 */
export class AddAttachment extends PopUp {
    constructor(props) {
        super(props);
        this.state = {
            file: null,
            title: '',
            comment: '',
            actionRunning: false,
            displayError: null,
            fileToBigMessage: ""
        }
        this.titleInputRef = React.createRef();
        this.ok = this.ok.bind(this);
        this.buildContent = this.buildContent.bind(this);
        this.fileAdded = this.fileAdded.bind(this);
        this.focusFirst = this.focusFirst.bind(this);
    }

    fileAdded(event) {
        //there will always be only one file
        this.setState({
            file: event.target.files[0],
            fileToBigMessage: undefined
        });
    }

    ok() {
        this.setState({actionRunning: true});
        const self = this;
        this.props.tasks.forEach(({key}) => {
            Api.addAttachment(key, encodeURIComponent(this.state.title), encodeURIComponent(this.state.comment), this.state.file).then(function (response) {
                    let documentName = JSON.parse(response)?.text || '';
                    self.props.updateTaskDetailsAfterAction(key, TASK_ACTIONS.ADD_ATTACHMENT);
                    self.props.showNotification(documentName + " " + self.props.translate("popUp.addAttachment.hasBeenAdded"));
                    self.props.closeCallback();
                },
                function (error) {
                    self.setState({
                        fileToBigMessage: mapErrorMessage(error),
                        actionRunning: false
                    });
                }
            );
        });

    }

    focusFirst() {
        this.titleInputRef.current?.focus();
    }

    componentDidMount() {
        super.componentDidMount();
        this.focusFirst();
    }

    buildContent() {
        const okEnabled = !this.state.actionRunning && this.state.comment && this.state.title && this.state.file && !this.state.fileToBigMessage;

        let fileName = "";
        let isPDFAllowed = this.props.taskDetail.taskPermission?.allowPdfUpload === true;
        let isHTMLAllowed = this.props.taskDetail.taskPermission?.allowHtmlUpload === true;
        let isTIFFAllowed = this.props.taskDetail.taskPermission?.allowTiffUpload === true;
        let fileLabelClass = this.state.fileToBigMessage ? "text-danger me-2" : "text-disabled me-2";
        let acceptedFileExtensions = isPDFAllowed ? ".png, .pdf, .jpg, .jpeg" : ".png, .jpg, .jpeg";
        let acceptedFilesHint = isPDFAllowed ? "popUp.addAttachment.browseHint" : "popUp.addAttachment.browseHintPNG";
        let acceptedFilesHintAdditionalTypes = "";
        if (isHTMLAllowed) {
            acceptedFileExtensions += ", .html";
            acceptedFilesHintAdditionalTypes += ", HTML";
        }
        if (isTIFFAllowed) {
            acceptedFileExtensions += ", .tif, .tiff";
            acceptedFilesHintAdditionalTypes += ", TIFF";
        }

        if (this.state.file != null)
            fileName = this.state.file.name;
        return (
            <div>
                {this.state.displayError && <ErrorDialog
                    error={this.state.displayError}
                    closeCallback={() => this.setState({actionRunning: false, displayError: null})}
                    translate={this.props.translate}
                />}
                <div className="modal-body ast_add_attachments px-0" aria-modal="true">
                    <div className="row w-100 m-0 p-0">
                        <FormField
                            id="title"
                            label={this.props.translate("popUp.addAttachment.fileTitleLabel")}
                            labelState={`(${this.props.translate("popUp.required")})`}>
                            <TextInput
                                ref={this.titleInputRef}
                                value={this.state.title}
                                onChange={(e) => this.setState({title: e.target.value})}
                                tabIndex={TAB_INDEX}
                            />
                        </FormField>
                    </div>

                    <div className="row w-100 m-0 p-0">
                        <Comment
                            inputHint={this.props.translate("popUp.approve.commentHint")}
                            inputLimit={256}
                            translate={this.props.translate}
                            tabIndex={TAB_INDEX + 1}
                            propagateValue={(value) => this.setState({comment: value})}
                            hideInitialValue={true}
                            id="comment"
                            label={this.props.translate("popUp.addAttachment.commentLabel")}
                            labelState={`(${this.props.translate("popUp.required")})`}
                        />
                    </div>

                    <div className="row w-100 m-0 p-0">
                        <FormField
                            id="fileInputBrowse"
                            label={this.props.translate("popUp.addAttachment.fileLabel")}
                            labelState={`(${this.props.translate("popUp.required")})`}>
                            <div className="d-flex align-items-center">
                                <span className={fileLabelClass}>{fileName}</span>
                                <label htmlFor="fileInput" tabIndex={TAB_INDEX + 2} className="btn btn-default">
                                    <input id="fileInput" type="file" style={{display: "none"}}
                                           accept={acceptedFileExtensions}
                                           onChange={this.fileAdded}/>
                                    {this.props.translate("popUp.addAttachment.browse")}
                                </label>
                            </div>
                        </FormField>
                    </div>

                    <div className="row w-100 m-0 p-0">
                        <span className="text-disabled d-block">
                          {this.props.translate(acceptedFilesHint)}{acceptedFilesHintAdditionalTypes}
                         </span>
                        <span className="text-danger d-block">
                            {this.state.fileToBigMessage}
                        </span>
                    </div>
                </div>

                <div className="modal-footer">
                    <div className="d-flex gap-8 float-right">
                        <Button type={ButtonType.PRIMARY}
                                onClick={this.ok}
                                disabled={!okEnabled}
                                tabIndex={TAB_INDEX + 4}
                                text={this.props.translate("popUp.ok")}
                        />
                        <Button type={ButtonType.SECONDARY}
                                onClick={this.props.closeCallback}
                                onBlur={this.focusFirst}
                                tabIndex={TAB_INDEX + 4}
                                text={this.props.translate("popUp.cancel")}
                        />
                    </div>
                </div>
            </div>
        )
            ;
    }

    getType() {
        return TASK_ACTIONS.ADD_ATTACHMENT;
    }

}

AddAttachment.propTypes = {
    tasks: PropTypes.array, // selected tasks
    closeCallback: PropTypes.func, //callback for closing the popup
    translate: PropTypes.func, // to translate texts
}

const withTranslations = translate(AddAttachment);
const mapStateToProps = function (store) {
    return {
        taskDetail: reduxSelectors.getTaskDetails(store)
    }
};

const connected = connect(mapStateToProps, {
    getTaskDetails,
    updateTaskDetailsAfterAction
})(withTranslations);

export default connected;
